import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

import { FieldError } from "@/components/ui/field-error";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNavigation } from "@/components/layout/app-layout";
import { useStockCosting } from "@/hooks/use-stock-costing";
import { toast } from "sonner";
import { Plus, Trash2, Package, ChevronDown, Info } from "lucide-react";
import { cn } from "@/lib/utils";

// Mock stock items data
const mockStockItems = [
  { code: "STK001", description: "Laptop Dell XPS 15", unitPrice: 1299.99, unitOfMeasure: "EA" },
  { code: "STK002", description: "Office Chair Ergonomic", unitPrice: 349.99, unitOfMeasure: "EA" },
  { code: "STK003", description: "A4 Paper Ream (500 sheets)", unitPrice: 4.99, unitOfMeasure: "RM" },
  { code: "STK004", description: "Steel Rods 10mm x 6m", unitPrice: 45.50, unitOfMeasure: "EA" },
  { code: "STK005", description: "Wireless Mouse Logitech", unitPrice: 29.99, unitOfMeasure: "EA" },
  { code: "STK006", description: "Standing Desk Adjustable", unitPrice: 599.99, unitOfMeasure: "EA" },
  { code: "STK007", description: "Ballpoint Pens Blue (Box of 50)", unitPrice: 12.99, unitOfMeasure: "BX" },
  { code: "STK008", description: "Aluminum Sheets 2mm", unitPrice: 89.99, unitOfMeasure: "SH" },
  { code: "STK009", description: "Monitor 27\" 4K Dell", unitPrice: 449.99, unitOfMeasure: "EA" },
  { code: "STK010", description: "Filing Cabinet 4-Drawer", unitPrice: 199.99, unitOfMeasure: "EA" },
];

// Mock projects data (from Financials/Dimensions)
const mockProjects = [
  { code: "PRJ-001", name: "Website Redesign 2024", type: "project" },
  { code: "PRJ-002", name: "Factory Expansion Phase 2", type: "project" },
  { code: "PRJ-003", name: "Client ABC - Custom Solution", type: "project" },
  { code: "PRJ-004", name: "Office Relocation", type: "project" },
  { code: "PRJ-005", name: "ERP Implementation", type: "project" },
];

// Mock cost centers data (from Financials/Dimensions)
const mockCostCenters = [
  { code: "CC-001", name: "Marketing Department", department: "Marketing" },
  { code: "CC-002", name: "IT Operations", department: "IT" },
  { code: "CC-003", name: "Production Line A", department: "Manufacturing" },
  { code: "CC-004", name: "Sales Team", department: "Sales" },
  { code: "CC-005", name: "R&D Department", department: "Research" },
  { code: "CC-006", name: "Warehouse Operations", department: "Logistics" },
];

// Mock warehouses data
const mockWarehouses = [
  { code: "WH-A", name: "Warehouse A", active: true },
  { code: "WH-B", name: "Warehouse B", active: true },
  { code: "WH-C", name: "Warehouse C", active: true },
  { code: "RS", name: "Retail Store", active: true },
  { code: "DC", name: "Distribution Center", active: true },
];

// Mock locations data
const mockLocations = [
  { locationCode: "A01-R01-S01", warehouseCode: "WH-A", locationType: "Storage", active: true },
  { locationCode: "A01-R01-S02", warehouseCode: "WH-A", locationType: "Picking", active: true },
  { locationCode: "A01-R02-S01", warehouseCode: "WH-A", locationType: "Storage", active: true },
  { locationCode: "B01-R01-S01", warehouseCode: "WH-B", locationType: "Storage", active: true },
  { locationCode: "B01-R02-B01", warehouseCode: "WH-B", locationType: "Picking", active: true },
  { locationCode: "C01-MAIN", warehouseCode: "WH-C", locationType: "Storage", active: true },
  { locationCode: "RS-MAIN", warehouseCode: "RS", locationType: "Retail Floor", active: true },
  { locationCode: "DC-RCV-01", warehouseCode: "DC", locationType: "Receiving", active: true },
  { locationCode: "DC-SHP-01", warehouseCode: "DC", locationType: "Shipping", active: true },
];

interface LineItem {
  id: string;
  stockCode: string;
  stockDescription: string;
  warehouse: string;
  location: string;
  movementType: "In" | "Out";
  quantity: string;
  unitPrice: string;
  unit: string;
  lineTotal: number;
  projectCode: string;
  costCenter: string;
  errors: {
    stockCode?: string;
    warehouse?: string;
    location?: string;
    quantity?: string;
    unitPrice?: string;
  };
}

interface FormData {
  referenceNumber: string;
  movementDate: Date;
  customerReference: string;
  notes: string;
}

interface FormErrors {
  referenceNumber?: string;
  movementDate?: string;
  customerReference?: string;
  notes?: string;
}

// Sales Movement Page - Updated: 2025-12-10 - Warehouse → Location Hierarchy
export function SalesMovementPage() {
  const { navigateTo } = useNavigation();
  const { processSale, valuationMethod, getValuationMethodDisplayName } = useStockCosting();
  
  // Prepare warehouse options for combobox
  const warehouseOptions: ComboboxOption[] = mockWarehouses
    .filter(w => w.active)
    .map(w => ({
      value: w.code,
      label: w.name,
      description: w.code,
    }));

  // Get filtered locations for a specific warehouse
  const getLocationOptionsForWarehouse = (warehouseCode: string): ComboboxOption[] => {
    if (!warehouseCode) return [];
    
    return mockLocations
      .filter(l => l.active && l.warehouseCode === warehouseCode)
      .map(l => ({
        value: l.locationCode,
        label: l.locationCode,
        description: l.locationType,
      }));
  };

  // Prepare stock item options for combobox
  const stockItemOptions: ComboboxOption[] = mockStockItems.map(item => ({
    value: item.code,
    label: `${item.code} - ${item.description}`,
    description: `${item.unitOfMeasure} | £${item.unitPrice.toFixed(2)}`,
  }));

  // Prepare project options for combobox
  const projectOptions: ComboboxOption[] = mockProjects.map(project => ({
    value: project.code,
    label: `${project.code} - ${project.name}`,
    description: project.type,
  }));

  // Prepare cost center options for combobox
  const costCenterOptions: ComboboxOption[] = mockCostCenters.map(cc => ({
    value: cc.code,
    label: `${cc.code} - ${cc.name}`,
    description: cc.department,
  }));
  
  const [formData, setFormData] = useState<FormData>({
    referenceNumber: "",
    movementDate: new Date(),
    customerReference: "",
    notes: "",
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [lineItems, setLineItems] = useState<LineItem[]>([
    {
      id: "1",
      stockCode: "",
      stockDescription: "",
      warehouse: "",
      location: "",
      movementType: "Out",
      quantity: "",
      unitPrice: "",
      unit: "",
      lineTotal: 0,
      projectCode: "",
      costCenter: "",
      errors: {},
    },
  ]);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const referenceInputRef = useRef<HTMLInputElement>(null);

  // Focus on reference number field on mount
  useEffect(() => {
    referenceInputRef.current?.focus();
  }, []);

  // Track unsaved changes
  useEffect(() => {
    const hasData =
      formData.referenceNumber ||
      formData.customerReference ||
      formData.notes ||
      lineItems.some(
        (item) => item.stockCode || item.quantity || item.unitPrice || item.location
      );
    setHasUnsavedChanges(hasData);
  }, [formData, lineItems]);



  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Calculate line total
  const calculateLineTotal = (quantity: string, unitPrice: string): number => {
    const qty = parseFloat(quantity) || 0;
    const price = parseFloat(unitPrice) || 0;
    return qty * price;
  };

  // Calculate summary totals
  const calculateSummary = () => {
    const totalItems = lineItems.length;
    const totalQuantity = lineItems.reduce(
      (sum, item) => sum + (parseFloat(item.quantity) || 0),
      0
    );
    const totalValue = lineItems.reduce((sum, item) => sum + item.lineTotal, 0);
    return { totalItems, totalQuantity, totalValue };
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;

    // Validate reference number
    if (!formData.referenceNumber.trim()) {
      errors.referenceNumber = "Reference number is required";
      isValid = false;
    }

    // Validate movement date
    if (!formData.movementDate) {
      errors.movementDate = "Movement date is required";
      isValid = false;
    } else if (formData.movementDate > new Date()) {
      errors.movementDate = "Movement date cannot be in the future";
      isValid = false;
    }

    // Validate customer reference length
    if (formData.customerReference.length > 100) {
      errors.customerReference = "Customer reference must be 100 characters or less";
      isValid = false;
    }

    // Validate notes length
    if (formData.notes.length > 500) {
      errors.notes = "Notes must be 500 characters or less";
      isValid = false;
    }

    setFormErrors(errors);

    // Validate line items
    const updatedLineItems = lineItems.map((item) => {
      const itemErrors: LineItem["errors"] = {};

      if (!item.stockCode) {
        itemErrors.stockCode = "Stock item is required";
        isValid = false;
      }

      const qty = parseFloat(item.quantity);
      if (!item.quantity || isNaN(qty) || qty <= 0) {
        itemErrors.quantity = "Quantity must be greater than 0";
        isValid = false;
      }

      return { ...item, errors: itemErrors };
    });

    setLineItems(updatedLineItems);

    return isValid;
  };

  // Handle form field changes
  const handleFieldChange = (field: keyof FormData, value: string | Date) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // Handle warehouse change for line items
  const handleWarehouseChange = (id: string, warehouseCode: string) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              warehouse: warehouseCode,
              location: "", // Reset location when warehouse changes
              errors: { ...item.errors, warehouse: undefined, location: undefined },
            }
          : item
      )
    );
  };

  // Handle line item changes
  const handleLineItemChange = (
    id: string,
    field: keyof LineItem,
    value: string
  ) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        const updatedItem = { ...item, [field]: value };

        // Auto-populate unit price and unit when stock item is selected
        if (field === "stockCode") {
          const stockItem = mockStockItems.find((s) => s.code === value);
          if (stockItem) {
            updatedItem.stockDescription = stockItem.description;
            updatedItem.unitPrice = stockItem.unitPrice.toFixed(2);
            updatedItem.unit = stockItem.unitOfMeasure;
          }
        }

        // Recalculate line total
        if (field === "quantity" || field === "unitPrice") {
          updatedItem.lineTotal = calculateLineTotal(
            updatedItem.quantity,
            updatedItem.unitPrice
          );
        }

        // Clear error for this field
        if (updatedItem.errors[field as keyof LineItem["errors"]]) {
          updatedItem.errors = {
            ...updatedItem.errors,
            [field]: undefined,
          };
        }

        return updatedItem;
      })
    );
  };

  // Add new line item
  const handleAddLineItem = () => {
    if (lineItems.length >= 50) {
      toast.error("Maximum 50 items per movement");
      return;
    }

    const newItem: LineItem = {
      id: Date.now().toString(),
      stockCode: "",
      stockDescription: "",
      warehouse: "",
      location: "",
      movementType: "Out",
      quantity: "",
      unitPrice: "",
      unit: "",
      lineTotal: 0,
      projectCode: "",
      costCenter: "",
      errors: {},
    };

    setLineItems((prev) => [...prev, newItem]);

    // Focus on the new row's stock code select after a brief delay
    setTimeout(() => {
      const newRow = document.querySelector(`[data-line-id="${newItem.id}"]`);
      const selectTrigger = newRow?.querySelector('[role="combobox"]') as HTMLElement;
      selectTrigger?.focus();
    }, 100);
  };

  // Remove line item
  const handleRemoveLineItem = (id: string) => {
    if (lineItems.length === 1) {
      return; // Cannot remove last line
    }

    setLineItems((prev) => prev.filter((item) => item.id !== id));
  };

  // Handle cancel
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    } else {
      navigateTo("stock-movements");
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector('[aria-invalid="true"]');
      firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
      (firstError as HTMLElement)?.focus();
      
      // Count errors
      const errorCount = 
        Object.keys(formErrors).length + 
        lineItems.reduce((sum, item) => sum + Object.keys(item.errors).length, 0);
      
      toast.error(`Please fix ${errorCount} error${errorCount > 1 ? 's' : ''} before saving`);
      return;
    }

    setIsSaving(true);

    try {
      console.log(`💰 Processing sales using ${getValuationMethodDisplayName()}`);

      // Process COGS calculations for each line item
      const movements = lineItems.map((item) => {
        // In a real app, you'd fetch the current stock item data from the API
        const mockStockItem = {
          code: item.stockCode,
          physicalQuantity: 200, // Mock current quantity
          averageCost: parseFloat(item.unitPrice) * 0.8, // Mock average cost (80% of selling price)
        };

        // Calculate COGS using FIFO or Average Cost
        const saleResult = processSale(
          mockStockItem as any,
          parseFloat(item.quantity),
          parseFloat(item.unitPrice),
          'DEFAULT' // In real app, get from location
        );

        if (valuationMethod === 'fifo') {
          console.log(`✅ FIFO Sale: ${item.stockCode} - Qty: ${item.quantity}, Selling Price: £${item.unitPrice}, COGS: £${saleResult.cogs.toFixed(2)}, Cost/Unit: £${saleResult.costPerUnit.toFixed(4)}, Margin: ${saleResult.margin.toFixed(2)}%`);
          if (saleResult.layersUsed && saleResult.layersUsed.length > 0) {
            console.log(`   📊 Consumed from ${saleResult.layersUsed.length} cost layer(s):`, saleResult.layersUsed);
          }
        } else {
          console.log(`✅ Average Cost Sale: ${item.stockCode} - Qty: ${item.quantity}, Selling Price: £${item.unitPrice}, COGS: £${saleResult.cogs.toFixed(2)}, Avg Cost: £${saleResult.costPerUnit.toFixed(4)}, Margin: ${saleResult.margin.toFixed(2)}%`);
        }

        return {
          id: `MOV${Date.now()}-${item.id}`,
          reference: formData.referenceNumber,
          date: formData.movementDate,
          type: "Sales",
          source: "Manual",
          stockCode: item.stockCode,
          stockDescription: item.stockDescription,
          location: item.location || null,
          quantity: -parseFloat(item.quantity), // Negative for sales
          unitPrice: parseFloat(item.unitPrice),
          lineTotal: -item.lineTotal, // Negative for sales
          cogs: saleResult.cogs,
          costPerUnit: saleResult.costPerUnit,
          margin: saleResult.margin,
          markup: saleResult.markup,
          userName: "John Smith",
          notes: formData.notes || null,
          customerReference: formData.customerReference || null,
        };
      });

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500));

      console.log("Created movements:", movements);

      setIsSaving(false);
      toast.success(`Sales movement created successfully - ${lineItems.length} item${lineItems.length > 1 ? 's' : ''} recorded`);
      navigateTo("stock-movements");
    } catch (error) {
      setIsSaving(false);
      toast.error("Failed to create sales movement. Please try again.");
      console.error("Error creating sales movement:", error);
    }
  };

  const summary = calculateSummary();

  return (
    <div className="space-y-6 pb-24">
      {/* Page Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Sales Movement</h1>
          <Badge variant="default" className="bg-blue-500 hover:bg-blue-600">
            Sales
          </Badge>
          <Badge 
            variant="outline" 
            className={valuationMethod === 'fifo' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-green-50 text-green-700 border-green-200'}
          >
            <Info className="h-3 w-3 mr-1" />
            {getValuationMethodDisplayName()}
          </Badge>
        </div>
        <p className="text-muted-foreground">
          Record stock sold to customers - decreases inventory levels • COGS calculated using {getValuationMethodDisplayName()}
        </p>
      </div>

      {/* Valuation Method Info Alert */}
      {valuationMethod === 'fifo' && (
        <Alert className="border-purple-200 bg-purple-50">
          <Info className="h-4 w-4 text-purple-600" />
          <AlertTitle className="text-purple-900">FIFO Costing Active</AlertTitle>
          <AlertDescription className="text-purple-700">
            Cost of Goods Sold (COGS) will be calculated by consuming from the oldest cost layers first (FIFO method).
            This ensures accurate profit margins based on actual purchase costs. Check the console to see which cost layers are being consumed.
          </AlertDescription>
        </Alert>
      )}

      {/* Movement Header Section */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Reference Number */}
          <div className="space-y-2">
            <Label htmlFor="referenceNumber">
              Reference Number <span className="text-destructive">*</span>
            </Label>
            <Input
              ref={referenceInputRef}
              id="referenceNumber"
              type="text"
              placeholder="e.g., SO-2024-001, INV-12345"
              maxLength={50}
              value={formData.referenceNumber}
              onChange={(e) => handleFieldChange("referenceNumber", e.target.value)}
              aria-required="true"
              aria-invalid={!!formErrors.referenceNumber}
              aria-describedby={formErrors.referenceNumber ? "referenceNumber-error" : "referenceNumber-help"}
              className={cn(formErrors.referenceNumber && "border-destructive")}
            />
            <p id="referenceNumber-help" className="text-sm text-muted-foreground">
              Sales order or invoice number
            </p>
            {formErrors.referenceNumber && (
              <FieldError id="referenceNumber-error" message={formErrors.referenceNumber} />
            )}
          </div>

          {/* Movement Date */}
          <div className="space-y-2">
            <Label htmlFor="movementDate">
              Movement Date <span className="text-destructive">*</span>
            </Label>
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="movementDate"
                  variant="outline"
                  className={cn(
                    "w-full justify-between font-normal",
                    !formData.movementDate && "text-muted-foreground",
                    formErrors.movementDate && "border-destructive"
                  )}
                  aria-required="true"
                  aria-invalid={!!formErrors.movementDate}
                  aria-describedby={formErrors.movementDate ? "movementDate-error" : undefined}
                >
                  {formData.movementDate
                    ? new Intl.DateTimeFormat("en-GB").format(formData.movementDate)
                    : "Select date"}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.movementDate}
                  onSelect={(date) => {
                    if (date) {
                      handleFieldChange("movementDate", date);
                      setDatePickerOpen(false);
                    }
                  }}
                  disabled={(date) => date > new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {formErrors.movementDate && (
              <FieldError id="movementDate-error" message={formErrors.movementDate} />
            )}
          </div>

          {/* Customer Reference */}
          <div className="space-y-2">
            <Label htmlFor="customerReference">Customer Reference</Label>
            <Input
              id="customerReference"
              type="text"
              placeholder="Customer name or code"
              maxLength={100}
              value={formData.customerReference}
              onChange={(e) => handleFieldChange("customerReference", e.target.value)}
              aria-invalid={!!formErrors.customerReference}
              aria-describedby={formErrors.customerReference ? "customerReference-error" : undefined}
              className={cn(formErrors.customerReference && "border-destructive")}
            />
            {formErrors.customerReference && (
              <FieldError id="customerReference-error" message={formErrors.customerReference} />
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Additional notes about this sale"
              maxLength={500}
              rows={3}
              value={formData.notes}
              onChange={(e) => handleFieldChange("notes", e.target.value)}
              aria-invalid={!!formErrors.notes}
              aria-describedby={formErrors.notes ? "notes-error" : "notes-count"}
              className={cn(formErrors.notes && "border-destructive")}
            />
            <p id="notes-count" className="text-sm text-muted-foreground text-right">
              {formData.notes.length}/500 characters
            </p>
            {formErrors.notes && (
              <FieldError id="notes-error" message={formErrors.notes} />
            )}
          </div>
        </div>
      </Card>

      {/* Line Items Section */}
      <Card className="p-6">
        <div className="space-y-4">
          {/* Section Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold">Items Sold</h3>
              <p className="text-sm text-muted-foreground">
                Add stock items sold in this transaction
              </p>
            </div>
            <Button onClick={handleAddLineItem} variant="secondary">
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>

          {/* Table or Empty State */}
          {lineItems.length === 0 || !lineItems.some(item => item.stockCode) ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No items added yet</p>
              <Button onClick={handleAddLineItem}>
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Stock Item *</TableHead>
                    <TableHead className="w-[150px]">Warehouse</TableHead>
                    <TableHead className="w-[150px]">Location</TableHead>
                    <TableHead className="w-[120px]">Movement Type *</TableHead>
                    <TableHead className="w-[100px]">Quantity *</TableHead>
                    <TableHead className="w-[100px]">Unit Price</TableHead>
                    <TableHead className="w-[80px]">Unit</TableHead>
                    <TableHead className="w-[100px]">Line Total</TableHead>
                    <TableHead className="w-[150px]">Project Code</TableHead>
                    <TableHead className="w-[150px]">Cost Center</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineItems.map((item) => (
                    <TableRow key={item.id} data-line-id={item.id}>
                      {/* Stock Item */}
                      <TableCell>
                        <div className="space-y-1">
                          <Combobox
                            options={stockItemOptions}
                            value={item.stockCode}
                            onValueChange={(value) => handleLineItemChange(item.id, "stockCode", value)}
                            placeholder="Search stock..."
                            searchPlaceholder="Search by code or name..."
                            emptyText="No stock items found."
                            className={cn(item.errors.stockCode && "border-destructive")}
                          />
                          {item.errors.stockCode && (
                            <FieldError message={item.errors.stockCode} />
                          )}
                        </div>
                      </TableCell>

                      {/* Warehouse */}
                      <TableCell>
                        <div className="space-y-1">
                          <Combobox
                            options={warehouseOptions}
                            value={item.warehouse}
                            onValueChange={(value) => handleWarehouseChange(item.id, value)}
                            placeholder="Select warehouse..."
                            searchPlaceholder="Search warehouses..."
                            emptyText="No warehouses found."
                            className={cn(item.errors.warehouse && "border-destructive")}
                          />
                          {item.errors.warehouse && (
                            <FieldError message={item.errors.warehouse} />
                          )}
                        </div>
                      </TableCell>

                      {/* Location */}
                      <TableCell>
                        <div className="space-y-1">
                          <Combobox
                            options={getLocationOptionsForWarehouse(item.warehouse)}
                            value={item.location}
                            onValueChange={(value) =>
                              handleLineItemChange(item.id, "location", value)
                            }
                            placeholder="Select location..."
                            searchPlaceholder="Search locations..."
                            emptyText={item.warehouse ? "No locations found." : "Select warehouse first"}
                            disabled={!item.warehouse}
                            className={cn(item.errors.location && "border-destructive")}
                          />
                          {item.errors.location && (
                            <FieldError message={item.errors.location} />
                          )}
                        </div>
                      </TableCell>

                      {/* Movement Type */}
                      <TableCell>
                        <Select
                          value={item.movementType}
                          onValueChange={(value: "In" | "Out") =>
                            handleLineItemChange(item.id, "movementType", value)
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="In">In</SelectItem>
                            <SelectItem value="Out">Out</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>

                      {/* Quantity */}
                      <TableCell>
                        <div className="space-y-1">
                          <Input
                            type="number"
                            placeholder="0"
                            min="0.01"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) =>
                              handleLineItemChange(item.id, "quantity", e.target.value)
                            }
                            className={cn(
                              "w-full",
                              item.errors.quantity && "border-destructive"
                            )}
                            aria-required="true"
                            aria-invalid={!!item.errors.quantity}
                          />
                          {item.errors.quantity && (
                            <FieldError message={item.errors.quantity} />
                          )}
                        </div>
                      </TableCell>

                      {/* Unit Price */}
                      <TableCell>
                        <div className="space-y-1">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                              £
                            </span>
                            <Input
                              type="text"
                              placeholder="0.00"
                              value={item.unitPrice}
                              readOnly
                              className="w-full pl-7 bg-muted cursor-not-allowed"
                            />
                          </div>
                        </div>
                      </TableCell>

                      {/* Unit */}
                      <TableCell>
                        <Input
                          type="text"
                          value={item.unit}
                          readOnly
                          className="w-full bg-muted cursor-not-allowed text-center"
                          placeholder="-"
                        />
                      </TableCell>

                      {/* Line Total */}
                      <TableCell>
                        <div className="font-medium">{formatCurrency(item.lineTotal)}</div>
                      </TableCell>

                      {/* Project Code */}
                      <TableCell>
                        <Combobox
                          options={projectOptions}
                          value={item.projectCode}
                          onValueChange={(value) =>
                            handleLineItemChange(item.id, "projectCode", value)
                          }
                          placeholder="Select project..."
                          searchPlaceholder="Search projects..."
                          emptyText="No projects found."
                        />
                      </TableCell>

                      {/* Cost Center */}
                      <TableCell>
                        <Combobox
                          options={costCenterOptions}
                          value={item.costCenter}
                          onValueChange={(value) =>
                            handleLineItemChange(item.id, "costCenter", value)
                          }
                          placeholder="Select cost center..."
                          searchPlaceholder="Search cost centers..."
                          emptyText="No cost centers found."
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveLineItem(item.id)}
                          disabled={lineItems.length === 1}
                          className="hover:text-destructive"
                          aria-label="Remove item"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </Card>

      {/* Movement Summary */}
      <Card className="p-6 lg:sticky lg:top-4">
        <h3 className="text-lg font-bold mb-4">Movement Summary</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Total Items</span>
            <span className="text-2xl font-bold">{summary.totalItems}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Total Quantity</span>
            <span className="text-2xl font-bold">
              {summary.totalQuantity.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between items-center pt-4 border-t">
            <span className="text-sm text-muted-foreground">Total Value</span>
            <span className="text-3xl font-bold text-primary">
              {formatCurrency(summary.totalValue)}
            </span>
          </div>
        </div>
      </Card>

      {/* Sticky Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/80 border-t z-50">
        <div className="container max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Movement"}
            </Button>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Sales Movement?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => navigateTo("stock-movements")}
              className="bg-destructive hover:bg-destructive/90"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
